// What the Models surface reads for every model
// (02-DESIGN/05-inference/01-models-and-instances.md § the console: Models):
// a model is a workload running the install's inference image; what it is
// called, which provider and model answer it, and what apps ask for come from
// its settings document, or from discovery once it is ready; whether its key
// exists comes from the vault's list, which says a path exists without
// revealing a value. One read per model, with no catalogue kept beside the
// fleet.

import type { Bus } from "../../platform/bus.ts";
import type { Callout } from "../../platform/callouts.ts";
import { DOCUMENT_REQUIRED, DOCUMENT_SETTINGS, type Entry } from "../../wire/config.ts";
import {
  calloutOf,
  credentialPath,
  parseSettings,
  type CalloutRequirement,
  type Instance,
  type Settings,
} from "../../wire/inference.ts";
import type { Workload } from "../../wire/workloads.ts";
import { ambiguousNames, modelStatus, type Plain } from "./status.ts";

/** Model is one row of the Models surface. */
export interface Model {
  w: Workload;
  /** The settings document, when it exists and parses. */
  settings?: Settings;
  /** The settings entry, for the revision a save guards on; absent when there is none. */
  entry?: Entry;
  /** Whether the settings document exists at all, parsed or not. */
  hasSettings: boolean;
  hasKey: boolean;
  /** The content callout the required document names, when it names one. */
  callout?: CalloutRequirement;
  /** The ready instance discovery answered for this workload, when any. */
  instance?: Instance;
  /** What apps ask for: discovery's word when ready, the settings' otherwise. */
  name: string;
  displayName: string;
  provider: string;
  model: string;
  status: Plain;
}

/** isModelWorkload reports whether a workload is one of the tenant's models: it runs the install's inference image, or discovery answered for it. */
export function isModelWorkload(
  w: Workload,
  inferenceImage: string | undefined,
  discovered: ReadonlySet<string>,
): boolean {
  if (discovered.has(w.id)) {
    return true;
  }
  if (inferenceImage === undefined || inferenceImage === "") {
    return false;
  }
  return w.image === inferenceImage || w.digest === inferenceImage;
}

/** ModelFacts are what is read for one model beside the fleet and discovery. */
export interface ModelFacts {
  entry?: Entry;
  settings?: Settings;
  hasSettings: boolean;
  hasKey: boolean;
  /** The content callout the required document names, when it names one. */
  callout?: CalloutRequirement;
}

/**
 * readFacts reads one model's settings, its required document's callout and
 * its key presence; a refusal of any reads as absent, which the status column
 * names.
 */
export async function readFacts(bus: Bus, id: string): Promise<ModelFacts> {
  const [entry, required, key] = await Promise.all([
    bus
      .configuration()
      .get(id, DOCUMENT_SETTINGS)
      .then(
        (e) => e,
        () => undefined,
      ),
    bus
      .configuration()
      .get(id, DOCUMENT_REQUIRED)
      .then(
        (e) => e,
        () => undefined,
      ),
    bus
      .vault()
      .list(credentialPath(id))
      .then(
        (r) => r.paths.some((p) => p.path === credentialPath(id)),
        () => false,
      ),
  ]);
  const settings = entry?.value === undefined ? undefined : parseSettings(entry.value);
  const callout = calloutOf(required?.value);
  return {
    entry,
    settings,
    hasSettings: entry !== undefined,
    hasKey: key,
    ...(callout !== undefined && { callout }),
  };
}

/** needsCallouts reports whether any model's required document names a callout: only then is the callout discovery scattered. */
export function needsCallouts(facts: Iterable<ModelFacts>): boolean {
  for (const f of facts) {
    if (f.callout !== undefined) {
      return true;
    }
  }
  return false;
}

/**
 * assemble joins the workloads, their facts and discovery into rows, marking
 * the names two rows carry; callouts are the callout services discovery
 * answered, undefined when the console did not or could not ask.
 */
export function assemble(
  workloads: readonly Workload[],
  facts: ReadonlyMap<string, ModelFacts>,
  instances: readonly Instance[],
  callouts?: readonly Callout[],
): Model[] {
  const calloutNames = callouts === undefined ? undefined : new Set(callouts.map((c) => c.name));
  const byWorkload = new Map(instances.map((i) => [i.workload, i]));
  const partial = workloads.map((w) => {
    const f = facts.get(w.id);
    const instance = byWorkload.get(w.id);
    const name = instance?.name ?? f?.settings?.name ?? "";
    return { w, f, instance, name };
  });
  const ambiguous = ambiguousNames(partial.map((p) => p.name));
  return partial.map(({ w, f, instance, name }) => ({
    w,
    settings: f?.settings,
    entry: f?.entry,
    hasSettings: f?.hasSettings ?? false,
    hasKey: f?.hasKey ?? false,
    callout: f?.callout,
    instance,
    name,
    displayName: instance?.displayName ?? f?.settings?.["display-name"] ?? (name !== "" ? name : w.name),
    provider: instance?.provider ?? f?.settings?.provider ?? "",
    model: instance?.model ?? f?.settings?.model ?? "",
    status: modelStatus({
      workload: w,
      discovered: instance !== undefined,
      hasSettings: f?.hasSettings ?? false,
      hasKey: f?.hasKey ?? false,
      ambiguous: name !== "" && ambiguous.has(name),
      ...(f?.callout !== undefined && {
        callout: { name: f.callout.name, found: calloutNames?.has(f.callout.name) },
      }),
    }),
  }));
}
